import { z } from "zod";
import { type ThirdPartyApiFormatValidationResult, ThirdPartyApiValidationResult } from "./ThirdPartyApiValidation.js";

/** Values are the property names on `ThirdPartyApiCredential`. */
export enum ThirdPartyApiCredentialField {
    API_KEY = "apiKey",
    API_SECRET = "apiSecret",
}

export enum ThirdPartyApiServiceType {
    DAVIS_WEATHER_LINK = "DAVIS_WEATHER_LINK",
}

export type ThirdPartyApiCredentialValues = Partial<Record<ThirdPartyApiCredentialField, string>>;

/** The narrowed form of {@link ThirdPartyApiCredentialValues} a key/secret service's client needs.
 *  Produced by parsing values through the service's own `credentialFormat`. */
export type ApiKeyAndSecret = { apiKey: string; apiSecret: string };

/** Zod spec over a service's credential fields. Extra keys are stripped, so a whole credential
 *  document can be passed straight in and `notes` / SCD bookkeeping are ignored. */
export type ThirdPartyApiCredentialFormat = z.ZodObject<Record<string, z.ZodType<string | undefined>>>;

export type ThirdPartyApiServiceConfiguration = {
    /** Sole authority on which fields are required and what shape they take. */
    credentialFormat: ThirdPartyApiCredentialFormat;
};

/**
 * The pair of host-contacting checks a service needs. Implementations need backend infrastructure
 * this package cannot reach, so they are collected backend-side rather than held here.
 */
export type ThirdPartyApiRemoteChecks = {
    /**
     * Whether the credential authenticates at all, judged on a benign endpoint's status rather than
     * its body. Run after the format check passes.
     */
    validateRecognized: (values: ThirdPartyApiCredentialValues) => Promise<boolean>;
    /**
     * Whether the credential reaches the data the integration needs, judged on the body: scoped
     * privileges where the host offers them, otherwise whatever stands in for them there. Run after
     * `validateRecognized` resolves true.
     */
    validatePermissions: (values: ThirdPartyApiCredentialValues) => Promise<boolean>;
};

/**
 * Adding a service here is all the routes, the validation job, and the dashboard form need. Checked
 * against a total `Record`, so a new enum member without its configuration is a compile error, while
 * the concrete `credentialFormat` survives inference for `parse` to narrow against.
 * Display names live in the `thirdPartyApi` i18n catalog, not here.
 */
export const thirdPartyApiServiceConfigurations = {
    [ThirdPartyApiServiceType.DAVIS_WEATHER_LINK]: {
        // Presence only — WeatherLink's key shape is unconfirmed and a guessed pattern would reject
        // valid credentials.
        credentialFormat: z.object({
            [ThirdPartyApiCredentialField.API_KEY]: z.string().min(1),
            [ThirdPartyApiCredentialField.API_SECRET]: z.string().min(1),
        }),
    },
} satisfies Record<ThirdPartyApiServiceType, ThirdPartyApiServiceConfiguration>;

export const thirdPartyApiServiceTypes = Object.values(ThirdPartyApiServiceType);

export const thirdPartyApiCredentialFields = Object.values(ThirdPartyApiCredentialField);

/** Local, offline check — safe to run in the dashboard before submitting. */
export const validateCredentialFormat = (
    configuration: ThirdPartyApiServiceConfiguration,
    values: ThirdPartyApiCredentialValues,
): ThirdPartyApiFormatValidationResult =>
    configuration.credentialFormat.safeParse(values).success
        ? ThirdPartyApiValidationResult.VALID
        : ThirdPartyApiValidationResult.INVALID_FORMAT;

/** Derived from `credentialFormat` so it cannot disagree with it. */
export const requiredCredentialFieldsFor = (
    configuration: ThirdPartyApiServiceConfiguration,
): ThirdPartyApiCredentialField[] =>
    Object.entries(configuration.credentialFormat.shape)
        // A field is optional exactly when `undefined` parses — zod's replacement for `isOptional()`.
        .filter(([, fieldSchema]) => !fieldSchema.safeParse(undefined).success)
        .map(([field]) => field as ThirdPartyApiCredentialField);
